import React, { useState } from "react";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { ShieldCheck, Mail, Lock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const API_BASE = "https://app.fintogo.ai/api";
const brandBlue = "#1E88E5";
const brandGreen = "#2ED08A";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email.trim(),
          password: form.password,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "login_failed");
      }
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify({
        id: data.user?.id,
        name: data.user?.full_name,
        classId: data.user?.school_class,
        role: data.user?.user_type,
      }));

      // Set correct subject based on user permissions
      const hasRW = !!data.user?.has_rechnungswesen;
      const hasBW = !!data.user?.has_betriebswirtschaft;
      const savedSubject = localStorage.getItem('selectedSubject');
      const savedIsValid =
        (savedSubject === 'rw' && hasRW) ||
        (savedSubject === 'bw' && hasBW);
      if (!savedIsValid) {
        localStorage.setItem('selectedSubject', !hasRW && hasBW ? 'bw' : 'rw');
      }

      const role = data.user?.user_type;
      if (role === "teacher") {
        window.location.href = "/TeacherDashboard";
      } else {
        window.location.href = "/StudentDashboard";
      }
    } catch (err) {
      const map = {
        invalid_credentials: "E-Mail oder Passwort ist falsch.",
        missing_fields: "Bitte fülle beide Felder aus.",
        server_error: "Serverfehler. Bitte später erneut versuchen.",
      };
      setError(
        map[err.message] || "Anmeldung fehlgeschlagen. Bitte erneut versuchen."
      );
    }
    setLoading(false);
  };

  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{
        minHeight: "100vh",
        width: "100%",
        background: "linear-gradient(to bottom right, #e0f0ff, white)",
        padding: "2rem 0",
      }}
    >
      <Container style={{ maxWidth: 600, padding: "2rem 1rem" }}>
        <Row className="justify-content-center">
          <Col>
            <Card className="shadow border-0">
              <Card.Body className="p-4 p-md-5">
                <div className="d-flex align-items-center mb-4 gap-3">
                  <div
                    className="rounded-circle d-flex justify-content-center align-items-center"
                    style={{
                      width: 48,
                      height: 48,
                      background: brandBlue,
                    }}
                  >
                    <ShieldCheck className="text-white" size={24} />
                  </div>
                  <div>
                    <h1 className="h4 mb-1">Login</h1>
                    <p className="mb-0 text-muted">
                      Melde dich mit deinen Zugangsdaten an.
                    </p>
                  </div>
                </div>

                {error && (
                  <Alert variant="danger" className="mb-4">
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleLogin}>
                  <Form.Group className="mb-3" controlId="loginEmail">
                    <Form.Label>E-Mail-Adresse</Form.Label>
                    <div className="d-flex align-items-center border rounded px-2">
                      <Mail className="text-muted me-2" size={18} />
                      <Form.Control
                        type="email"
                        placeholder="name@schule.at"
                        value={form.email}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, email: e.target.value }))
                        }
                        className="border-0 shadow-none"
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="loginPassword">
                    <Form.Label>Passwort</Form.Label>
                    <div className="d-flex align-items-center border rounded px-2">
                      <Lock className="text-muted me-2" size={18} />
                      <Form.Control
                        type="password"
                        placeholder="Passwort"
                        value={form.password}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, password: e.target.value }))
                        }
                        className="border-0 shadow-none"
                      />
                    </div>
                  </Form.Group>

                  <div className="d-grid mb-3">
                    <Button
                      type="submit"
                      disabled={loading}
                      style={{
                        background: brandBlue,
                        borderColor: brandBlue,
                      }}
                    >
                      {loading ? (
                        "Wird gesendet..."
                      ) : (
                        <>
                          Anmelden
                          <ArrowRight className="ms-2" size={18} />
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="text-center text-muted small">
                    Noch kein Zugang?{" "}
                    <Link to="/" className="text-decoration-none">
                      Zurück zur Startseite
                    </Link>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
